import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { Close as CloseIcon } from '@mui/icons-material';
import { api } from '@/api/repository/axios';
import { designSystem } from '@/styles/theme';

interface ActiveAnnouncement {
    id: number;
    content: string;
    updatedAt: string;
}

const DISMISS_KEY_PREFIX = 'dismissed_announcement_';

const Bar = styled.div`
    position: relative;
    background: linear-gradient(90deg, ${designSystem.colors.primary} 0%, ${designSystem.colors.primaryDark} 100%);
    color: white;
    font-size: 12px;
    padding: 4px 36px 4px 12px;
    text-align: center;
    line-height: 1.4;
    word-break: keep-all;

    @media (max-width: 768px) {
        display: none;
    }
`;

const CloseButton = styled.button`
    position: absolute;
    top: 50%;
    right: 8px;
    transform: translateY(-50%);
    background: transparent;
    border: none;
    color: white;
    padding: 2px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    opacity: 0.8;

    &:hover {
        opacity: 1;
        background: rgba(255, 255, 255, 0.15);
    }

    & svg {
        font-size: 16px;
    }
`;

const TopAnnouncementBar: React.FC = () => {
    const [announcement, setAnnouncement] = useState<ActiveAnnouncement | null>(null);
    const [hidden, setHidden] = useState<boolean>(false);

    useEffect(() => {
        let cancelled = false;
        const fetchAnnouncement = async () => {
            try {
                const res = await api.announcement.getTop();
                if (cancelled) {
                    return;
                }
                const result = res?.result ?? null;
                if (!result) {
                    setAnnouncement(null);
                    return;
                }
                // sessionStorage 기준 dismiss 여부 확인
                const dismissKey = `${DISMISS_KEY_PREFIX}${result.id}`;
                const dismissed = typeof window !== 'undefined' && window.sessionStorage?.getItem(dismissKey);
                if (dismissed === '1') {
                    setAnnouncement(null);
                    return;
                }
                setAnnouncement(result);
            } catch (error) {
                // 실패 시 띠 자체를 노출하지 않음 (빈 영역 X)
                if (!cancelled) {
                    setAnnouncement(null);
                }
            }
        };
        fetchAnnouncement();
        return () => {
            cancelled = true;
        };
    }, []);

    const handleDismiss = () => {
        if (announcement && typeof window !== 'undefined' && window.sessionStorage) {
            window.sessionStorage.setItem(`${DISMISS_KEY_PREFIX}${announcement.id}`, '1');
        }
        setHidden(true);
    };

    if (!announcement || hidden) {
        return null;
    }

    return (
        <Bar>
            {announcement.content}
            <CloseButton type="button" aria-label="상단 안내 닫기" onClick={handleDismiss}>
                <CloseIcon />
            </CloseButton>
        </Bar>
    );
};

export default TopAnnouncementBar;
